import { useQuery } from '@tanstack/react-query';
import { request, queryString } from '../../../shared/api/httpClient';
import { ExactDecimal } from '../../../shared/lib/money';
import { useWorkspace } from '../../stores';
import type { reportMetrics } from '../model/reportMetrics';
export type FinancialReport = Omit<ReturnType<typeof reportMetrics>, 'staff'> & {
  customerIds:string[];
  invoiceTotal:number; outstanding:number; cancelledAmount:number; pendingExpenses:number;
  invoiceCount:number; draftCount:number; cancelledBillCount:number; expenseCount:number; pendingExpenseCount:number;
  expenseCategories:{id:string;title:string;amount:number}[];
  staff:(ReturnType<typeof reportMetrics>['staff'][number]&{commission?:number;count?:number})[];
  source:{kind:'server_snapshot';complete:true;generated_at:number;last_received_at:number|null;timezone:string;currency_code:string;from:string;to:string;notice:string;notice_code:string;unsynced_device_data_known:false};
};
function exact(value:unknown):number {
  if(typeof value!=='string' || !/^-?\d+(?:\.\d+)?$/.test(value)) throw new Error('Không thể tải dữ liệu. Vui lòng thử lại.');
  return ExactDecimal.from(value).toNumberExact();
}
export function parseFinancialReport(input:unknown):FinancialReport {
  if(!input || typeof input!=='object')throw new Error('Không thể tải dữ liệu. Vui lòng thử lại.');
  const report=input as Record<string,unknown>,source=report.source as FinancialReport['source'];
  if(source?.kind!=='server_snapshot'||source.complete!==true)throw new Error('Chưa tải đủ dữ liệu. Hãy thử lại để xem danh sách và xuất báo cáo.');
  const result={...report} as Record<string,unknown>;
  if(!Array.isArray(report.customerIds)||!report.customerIds.every(id=>typeof id==='string'))throw new Error('Không thể tải dữ liệu. Vui lòng thử lại.');
  for(const key of ['revenue','cost','net','invoiceTotal','outstanding','cancelledAmount','pendingExpenses'])result[key]=exact(report[key]);
  for(const key of ['margin','averageTicket'])result[key]=report[key]===null?null:exact(report[key]);
  for(const key of ['paidCount','invoiceCount','draftCount','cancelledBillCount','expenseCount','pendingExpenseCount','booked','completed','cancelled','noShows','confirmed','completedPaid','activeCustomers','newCustomers','returning','unknownCustomers','anonymousBills']) {
    if(!Number.isSafeInteger(report[key]) || (report[key] as number)<0)throw new Error('Không thể tải dữ liệu. Vui lòng thử lại.');
  }
  for(const key of ['series','services','staff','departments','expenseCategories']){
    if(!Array.isArray(report[key]))throw new Error('Không thể tải dữ liệu. Vui lòng thử lại.');
    result[key]=(report[key] as Record<string,unknown>[]).map(row=>({...row,...(key==='series'?{income:exact(row.income),expense:exact(row.expense)}:{amount:exact(row.amount)}),...(row.commission!==undefined?{commission:exact(row.commission)}:{})}));
  }
  return result as unknown as FinancialReport;
}
export function useFinancialReport(from:string,to:string,bucket:'day'|'hour'='day') {
  const {isDemo,store,scopeKey}=useWorkspace();
  return useQuery({queryKey:[...scopeKey,'financial-report',from,to,bucket],enabled:!isDemo&&Boolean(store?.id),staleTime:30_000,
    queryFn:async({signal})=>{
      const response=await request<{report:unknown}>('/bills/financial-report'+queryString({from,to,bucket}),{signal});
      const report=parseFinancialReport(response.report);
      if(report.source.from!==from||report.source.to!==to)throw new Error('Không thể tải dữ liệu. Vui lòng thử lại.');
      return report;
    }});
}
